import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Sparkles, Trash2 } from 'lucide-react';
import { agoLabel, type AskRun, type Idea, type IdeaAsk } from '../../utils/ideas';
import { NO_TEXT_MODEL, THINKING_LABELS, estimateCostUsd, formatCost, formatCostShort, formatPrices, screenplayRequest, thinkRequest, withThinking, type TextModelId, type TextModelOption } from '../../utils/textModel';
import { DEFAULT_GENERATOR, PROMPT_TARGETS, frameOf, framesOf, generatorOf, lengthOf, lengthsOf, promptGenerators, promptRequest, type PromptTarget } from '../../utils/promptWriter';
import { usePromptSkill } from '../../hooks/usePromptSkill';
import { BoardCard, errorText } from './parts';

interface AskCardProps {
  idea: Idea;
  canEdit: boolean;
  canDelete: boolean;
  /** The models the keys at hand can ask; none means no key */
  options: TextModelOption[];
  modelId: TextModelId | null;
  onModelChange: (id: TextModelId) => void;
  /** How hard a thinking model thinks (one of its efforts); null for a model that does not think */
  thinking?: string | null;
  onThinkingChange?: (effort: string) => void;
  /** The cards kept so far, offered as what to build on */
  keptTexts: string[];
  /** Asking now */
  busy: boolean;
  now: number;
  /** Just made: the prompt takes the cursor */
  autoFocus?: boolean;
  onAutoFocused?: () => void;
  /** Lit up: what a link asked for */
  highlighted?: boolean;
  onSaveAsk: (ask: Partial<IdeaAsk>) => Promise<void>;
  onAsk: () => Promise<void>;
  onDelete: () => Promise<void>;
}

const GROUPS: TextModelOption['group'][] = ['Venice', 'More on Venice', 'Google', 'OpenAI'];
const SAVE_AFTER_MS = 700;

const runWords = (run: AskRun): string => {
  const tokens = run.inputTokens !== null && run.outputTokens !== null ? `${(run.inputTokens + run.outputTokens).toLocaleString('en-US')} tokens` : null;
  return [`${run.model}`, tokens, run.usd !== null ? formatCost(run.usd) : null].filter(Boolean).join(', ');
};

/** A text area that is as tall as what is in it, and never short */
const fitHeight = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = '0px';
  el.style.height = `${Math.max(180, el.scrollHeight)}px`;
};

/**
 * A Think up card: whatever is written in it is asked of a model, with which model and at
 * what cost; each answer lands beside it as a card of its own, and the askings are remembered.
 */
export const AskCard: React.FC<AskCardProps> = ({
  idea, canEdit, canDelete, options, modelId, onModelChange, thinking = null, onThinkingChange, keptTexts, busy, now, autoFocus = false, onAutoFocused, highlighted = false, onSaveAsk, onAsk, onDelete,
}) => {
  const ask = idea.ask ?? { topic: '', buildOnKept: true };
  const [topic, setTopic] = useState(ask.topic);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const pending = useRef<string | null>(null);
  const timer = useRef<number | null>(null);
  const topicRef = useRef<HTMLTextAreaElement>(null);
  const available = options.length > 0;
  const model = options.find(option => option.id === modelId) ?? options[0] ?? null;
  const modelName = model?.name.replace(/ \(your .* key\)$/, '') ?? null;
  const kept = ask.buildOnKept ? keptTexts : [];
  const format = ask.format ?? 'card';
  // A prompt: for which node, by which generator, at what length and in what frame; the prompt writer's skill goes along with the asking
  const target: PromptTarget = ask.target ?? 'video';
  const generator = format === 'prompt' ? generatorOf(target, ask.generator) : null;
  const generators = format === 'prompt' ? promptGenerators(target) : [];
  const generatorGroups = Array.from(new Set(generators.map(option => option.group)));
  const skill = usePromptSkill(format === 'prompt' ? target : null);
  const length = target === 'video' ? lengthOf(generator, ask.length) : undefined;
  const frame = frameOf(generator, ask.frame);
  // (a thinking model's thinking is paid for too: the estimate counts the room it gets; a screenplay is asked for by the page)
  const request = format === 'screenplay' ? screenplayRequest('write', { brief: topic, pages: ask.pages ?? 2 })
    : format === 'prompt' ? promptRequest({ brief: topic, target, generator, duration: length, aspectRatio: frame, kept, skill: skill ?? '' })
    : thinkRequest(topic, kept);
  const cost = model ? estimateCostUsd(model, withThinking(request, model, thinking)) : null;
  const efforts = model?.reasoning ? (model.efforts?.length ? model.efforts : ['low', 'medium', 'high']) : [];
  const spent = idea.runs.reduce((sum, run) => sum + (run.usd ?? 0), 0);

  // Someone else's change to the prompt is taken when nothing of ours is unsaved
  useEffect(() => { if (pending.current === null) setTopic(ask.topic); }, [ask.topic]);
  useEffect(() => { fitHeight(topicRef.current); }, [topic]);
  useEffect(() => { if (autoFocus) { topicRef.current?.focus(); onAutoFocused?.(); } }, [autoFocus, onAutoFocused]);

  const flush = useCallback(async () => {
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
    const text = pending.current;
    if (text === null) return;
    pending.current = null;
    try { await onSaveAsk({ topic: text }); } catch (e) { setProblem(errorText(e, 'The card could not be saved.')); }
  }, [onSaveAsk]);
  useEffect(() => () => { void flush(); }, [flush]);
  const queueTopic = (text: string) => {
    setTopic(text);
    pending.current = text;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { void flush(); }, SAVE_AFTER_MS);
  };
  const change = (patch: Partial<IdeaAsk>) => { void onSaveAsk(patch).catch((e) => setProblem(errorText(e, 'The card could not be saved.'))); };

  const go = async () => {
    if (!available || busy || !canEdit) return;
    setProblem(null);
    await flush();
    try { await onAsk(); } catch (e) { setProblem(errorText(e, 'The model did not answer.')); }
  };

  return (
    <BoardCard
      id={idea.id}
      tone="flame"
      mark={<span className="canvas-node-icon"><Sparkles /></span>}
      title={<span className="canvas-node-title">Think up ideas</span>}
      tools={canDelete && !confirmDelete ? (
        <button type="button" onClick={() => setConfirmDelete(true)} className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-danger" aria-label="Delete this card" title="Delete this Think up card (the cards it wrote stay)">
          <Trash2 />
        </button>
      ) : null}
      highlighted={highlighted}
      data={{ 'data-ask-card': '' }}
    >
      {confirmDelete && (
        <div className="ns-note m-3" data-tone="danger" data-confirm-delete>
          <span className="flex-1">Delete this card? The cards it wrote stay.</span>
          <button type="button" onClick={() => { setConfirmDelete(false); void onDelete().catch((e) => setProblem(errorText(e, 'The card could not be deleted.'))); }} className="ns-btn ns-btn-xs ns-btn-danger ns-btn-flat">Delete</button>
          <button type="button" onClick={() => setConfirmDelete(false)} className="ns-btn ns-btn-xs ns-btn-soft ns-btn-flat">Keep</button>
        </div>
      )}

      <form className="flex flex-col gap-3 px-4 pt-3 pb-4" onSubmit={(e) => { e.preventDefault(); void go(); }} data-ask-form>
        {!available && canEdit && <div className="ns-note" data-tone="warn">{NO_TEXT_MODEL}</div>}
        <textarea
          ref={topicRef}
          value={topic}
          readOnly={!canEdit}
          onChange={(e) => queueTopic(e.target.value)}
          onBlur={() => void flush()}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void go(); } }}
          placeholder={format === 'prompt'
            ? (target === 'video'
              ? 'Describe the film: who is in it, what happens, where, the look, and which references you have (a character sheet, a prop sheet, an empty plate). The prompt writer turns it into fixed shots with timings, one job per reference, check lines and the sound, ready to paste into a Video node. Ctrl+Enter asks.'
              : 'Describe the picture: a character sheet, a prop sheet, an empty plate, an edit of a frame, a style, and which references you have. The prompt writer turns it into a prompt ready to paste into an Image node. Ctrl+Enter asks.')
            : 'Ask for anything: a story about a fisherman who finds a whale after his boat is destroyed and argues with it about life and death. Three loglines. A scene. Dialogue. The answer comes as a card beside this one. Ctrl+Enter asks.'}
          aria-label="What to ask"
          className="ns-field w-full resize-none font-serif text-[15px] leading-7"
          data-ask-topic
        />
        {available && canEdit && (
          <div>
            <select value={model?.id ?? ''} onChange={(e) => onModelChange(e.target.value)} aria-label="Which model" className="ns-field ns-field-sm w-full" data-text-model>
              {GROUPS.filter(group => options.some(option => option.group === group)).map(group => (
                <optgroup key={group} label={group}>
                  {options.filter(option => option.group === group).map(option => {
                    const prices = formatPrices(option);
                    return <option key={option.id} value={option.id}>{option.name}{prices ? ` · ${prices}` : ''}</option>;
                  })}
                </optgroup>
              ))}
            </select>
            {efforts.length > 0 && (
              <label className="flex items-center gap-2 mt-2 text-xs text-ink" data-thinking>
                <span className="shrink-0 text-ink-soft">Thinking</span>
                <select value={thinking ?? ''} onChange={(e) => onThinkingChange?.(e.target.value)} aria-label="How hard the model thinks" className="ns-field ns-field-sm flex-1" title="This model thinks before it answers. Deeper thinking is slower and costs more; light thinking is enough for most cards.">
                  {thinking === null && <option value="">The model's own</option>}
                  {efforts.map(effort => <option key={effort} value={effort}>{THINKING_LABELS[effort] ?? effort}</option>)}
                </select>
              </label>
            )}
            <span className="ns-t-meta block mt-1" data-cost-estimate>
              {format === 'prompt' && !skill ? 'Counting what this asking costs.' : cost !== null ? `This asking: up to ${formatCost(cost)} from your Venice balance.${efforts.length > 0 ? ' Thinking is paid for too.' : ''}` : `Billed to your own ${model?.group ?? ''} key.`}
              {format === 'screenplay' ? ' The screenplay lands beside this card, page 1 open.' : ''}
              {format === 'prompt' ? ` The prompt writer's method goes along with the asking. The prompt lands beside this card, its paste ready for ${target === 'video' ? 'a Video' : 'an Image'} node.` : ''}
            </span>
          </div>
        )}
        {canEdit && (
          <div className="flex flex-col gap-2" data-ask-format>
            <div className="flex items-center justify-between gap-2 text-xs text-ink">
              <span className="text-ink-soft font-semibold">Format</span>
              <div className="ns-seg" role="radiogroup" aria-label="What the answer is">
                {([['card', 'Card'], ['screenplay', 'Screenplay'], ['prompt', 'Prompt']] as const).map(([value, label]) => (
                  <button key={value} type="button" role="radio" aria-checked={(ask.format ?? 'card') === value} className="ns-seg-item" onClick={() => change({ format: value })}>{label}</button>
                ))}
              </div>
            </div>
            {ask.format === 'screenplay' && (
              <>
                <label className="flex items-center justify-between gap-2 text-xs text-ink">
                  <span className="text-ink-soft font-semibold">Length</span>
                  <select value={ask.pages ?? 2} onChange={(e) => change({ pages: Number(e.target.value) })} aria-label="How many pages" className="ns-field ns-field-sm w-[180px]">
                    {[1, 2, 3, 5, 8, 10, 15, 20].map(n => <option key={n} value={n}>{n} page{n === 1 ? '' : 's'} · about {n} min</option>)}
                  </select>
                </label>
                <div className="flex items-center justify-between gap-2 text-xs text-ink">
                  <span className="text-ink-soft font-semibold">Paper</span>
                  <div className="ns-seg" role="radiogroup" aria-label="Paper">
                    {(['A4', 'Letter'] as const).map(paper => (
                      <button key={paper} type="button" role="radio" aria-checked={(ask.paper ?? 'A4') === paper} className="ns-seg-item" onClick={() => change({ paper })}>{paper}</button>
                    ))}
                  </div>
                </div>
              </>
            )}
            {format === 'prompt' && (
              <>
                <div className="flex items-center justify-between gap-2 text-xs text-ink">
                  <span className="text-ink-soft font-semibold">For</span>
                  <div className="ns-seg" role="radiogroup" aria-label="Which node the prompt is for">
                    {PROMPT_TARGETS.map(option => (
                      <button key={option.id} type="button" role="radio" aria-checked={target === option.id} className="ns-seg-item" onClick={() => change({ target: option.id, generator: DEFAULT_GENERATOR[option.id] })}>{option.node}</button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center justify-between gap-2 text-xs text-ink">
                  <span className="text-ink-soft font-semibold">Generator</span>
                  <select value={generator?.id ?? ''} onChange={(e) => change({ generator: e.target.value })} aria-label="Which generator the prompt is for" className="ns-field ns-field-sm w-[180px]" data-prompt-generator>
                    {generatorGroups.map(group => (
                      <optgroup key={group} label={group}>
                        {generators.filter(option => option.group === group).map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </label>
                {target === 'video' && lengthsOf(generator).length > 0 && (
                  <label className="flex items-center justify-between gap-2 text-xs text-ink">
                    <span className="text-ink-soft font-semibold">Length</span>
                    <select value={length ?? ''} onChange={(e) => change({ length: e.target.value })} aria-label="How long the video is" className="ns-field ns-field-sm w-[180px]" data-prompt-length>
                      {lengthsOf(generator).map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </label>
                )}
                {framesOf(generator).length > 0 && (
                  <label className="flex items-center justify-between gap-2 text-xs text-ink">
                    <span className="text-ink-soft font-semibold">Frame</span>
                    <select value={frame ?? ''} onChange={(e) => change({ frame: e.target.value })} aria-label="The frame's shape" className="ns-field ns-field-sm w-[180px]" data-prompt-frame>
                      {framesOf(generator).map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </label>
                )}
                <p className="ns-t-meta" data-prompt-method>
                  Written by the generation prompt writer's method: {target === 'video' ? 'fixed shots with timings, one job per reference, a check line on every risky beat, the sound written out, and the content filter kept in mind' : 'natural prose, one job per reference, a count for everything a generator duplicates, and the content filter kept in mind'}. The references of this workspace go along by name.
                </p>
              </>
            )}
          </div>
        )}
        {keptTexts.length > 0 && canEdit && (
          <label className="flex items-center gap-2.5 text-xs text-ink">
            <input type="checkbox" className="ns-switch" checked={ask.buildOnKept} onChange={(e) => change({ buildOnKept: e.target.checked })} />
            Build on the {keptTexts.length === 1 ? 'card' : `${keptTexts.length} cards`} kept so far
          </label>
        )}
        {canEdit && (
          <button type="submit" disabled={!available || busy} className="ns-btn ns-btn-sm ns-btn-primary ns-btn-flat self-start" data-ask-go>
            {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Sparkles size={14} aria-hidden="true" />}
            {busy ? 'Writing' : modelName ? `Ask ${modelName}` : 'Ask'}
          </button>
        )}
        {problem && <div className="ns-note" data-tone="danger" role="alert">{problem}</div>}
      </form>

      {idea.runs.length > 0 && (
        <footer className="px-4 py-2.5 border-t border-ink/5" data-ask-runs>
          <span className="flex items-center gap-2">
            <span className="ns-t-label">Asked {idea.runs.length === 1 ? 'once' : `${idea.runs.length} times`}</span>
            {spent > 0 && <span className="ns-chip ns-chip-sm" title={`What the askings of this card cost so far: ${formatCost(spent)}`} data-ask-spent>{formatCostShort(spent)} so far</span>}
          </span>
          <ul className="mt-1 flex flex-col gap-0.5">
            {[...idea.runs].reverse().slice(0, 3).map((run, i) => (
              <li key={`${run.at}-${i}`} className="ns-t-meta">{agoLabel(run.at, now)}: {runWords(run)}</li>
            ))}
          </ul>
        </footer>
      )}
    </BoardCard>
  );
};

export default AskCard;
